import React from 'react';
import styled from 'styled-components';
import moment from 'moment';

import Select from '@/components/userOvertimes/Select';
import useLanguage from '@/components/common/hooks/useLanguage';
import useToggle from '@/components/common/hooks/useToggle';
import PillIcon from '@/components/common/icons/PillIcon';
import BinIcon from '@/components/common/icons/BinIcon';
import ModalDelete from '@/components/users/ModalDelete';
import { SickLeave } from '@/store/reducers/sick-leaves';

export interface SickLeavesProps{
  sickLeaves: SickLeave[]
}

interface Props {
  sickLeave: SickLeavesProps;
  onDelete?: (sickLeaveId: number) => void;
  deletableFrom?: string;
}

interface DayProps {
  sick: SickLeave;
  onDelete?: (sickLeaveId: number) => void;
  deletableFrom?: string;
}

const Day: React.FC<DayProps> = ({ sick, onDelete, deletableFrom }) => {
  const [{ common, inputsPages }] = useLanguage();
  const [isActiveModalDelete, toggleIsActiveModalDelete] = useToggle();

  const openDeleteModal = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.preventDefault();
    e.stopPropagation();
    toggleIsActiveModalDelete(e);
  };

  const handleDelete = () => {
    if (onDelete) onDelete(sick._id);
    toggleIsActiveModalDelete(false);
  };

  // Dates are stored as YYYY-MM-DD, so a plain string compare orders them.
  const canDelete = Boolean(onDelete)
    && (!deletableFrom || sick.date >= deletableFrom);

  return (
    <>
      <Select
        icon={<PillIcon />}
        title={moment(sick.date).format('Do, MMM YYYY')}
        selectContent={(
          <Wrapper>
            <ContentWrap>
              <Content>
                <Title>{inputsPages.comment}</Title>
                <Title>{sick.date}</Title>
              </Content>
              <RightContent>
                <UnitText>{sick.comment}</UnitText>
              </RightContent>
            </ContentWrap>
            {canDelete && (
              <DeleteWrap onClick={openDeleteModal}>
                <BinIcon color="#909599" />
                <DeleteText>{common.delete}</DeleteText>
              </DeleteWrap>
            )}
          </Wrapper>
        )}
      />
      {isActiveModalDelete && (
        <ModalDelete
          hideModal={toggleIsActiveModalDelete}
          deleteHandle={handleDelete}
        />
      )}
    </>
  );
};

const Item: React.FC<Props> = ({ sickLeave, onDelete, deletableFrom }) => (
  sickLeave ? (
    <>
      {sickLeave.sickLeaves.map((sick) => (
        <Day
          key={sick._id}
          sick={sick}
          onDelete={onDelete}
          deletableFrom={deletableFrom}
        />
      ))}
    </>
  ) : (
    <>
    </>
  )
);

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 30px;
  background-color: rgba(255, 255, 255, 0);
`;

const ContentWrap = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding: 5px;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-top: 0;
`;

const RightContent = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: 16px;
  margin-top: 10px;
  justify-content: center;
  margin-top: 0;
`;

const DeleteWrap = styled.div`
  display: flex;
  align-items: center;
  align-self: flex-end;
  cursor: pointer;
  padding: 5px;
  margin-top: 5px;
`;

const DeleteText = styled.span`
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: #909599;
  margin-left: 8px;
`;

const Title = styled.p`
  font-size: 14px;
  font-weight: 600;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.43;
  letter-spacing: normal;
  color: #21272e;
  text-transform: capitalize;

  @supports (-webkit-line-clamp: 2) {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: initial;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
`;

const UnitText = styled.span`
  font-size: 12px;
  font-weight: 600;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;

  text-transform: capitalize;
`;

export default Item;
